import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Shield, TrendingUp, Users, Award, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import FinancialTicker from '../components/FinancialTicker';
import OptimizedImage from '../components/OptimizedImage';
import { services, processSteps } from '../mockData';
import { testimonialsAPI, initiativesAPI } from '../services/api';

const Home = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All Initiatives');
  const [testimonials, setTestimonials] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const heroImages = [
    {
      url: 'https://images.unsplash.com/photo-1758519292135-2af0ad50f552?q=70&w=800&auto=format&fit=crop',
      alt: 'Professional using digital banking'
    },
    {
      url: 'https://images.unsplash.com/photo-1761735486587-bcac08b15c79?q=70&w=800&auto=format&fit=crop',
      alt: 'Modern banking workspace'
    },
    {
      url: 'https://images.unsplash.com/photo-1560472355-536de3962603?q=70&w=800&auto=format&fit=crop',
      alt: 'Financial technology and innovation'
    },
    {
      url: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=70&w=800&auto=format&fit=crop',
      alt: 'Professional banking services'
    },
    {
      url: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=70&w=800&auto=format&fit=crop',
      alt: 'Team collaboration in finance'
    }
  ];

  const categories = ['All Initiatives', 'Digital Banking', 'Corporate', 'Savings', 'Community'];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchInitiatives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  // Auto-rotate hero images every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 6000);
    
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [testimonialsData, initiativesData] = await Promise.all([
        testimonialsAPI.getAll(),
        initiativesAPI.getAll()
      ]);
      setTestimonials(testimonialsData);
      setInitiatives(initiativesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInitiatives = async () => {
    try {
      const category = selectedCategory === 'All Initiatives' ? null : selectedCategory;
      const data = await initiativesAPI.getAll(category);
      setInitiatives(data);
    } catch (error) {
      console.error('Error fetching initiatives:', error);
    }
  };

  const filteredInitiatives = initiatives;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-white pt-32 pb-20 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full filter blur-3xl opacity-30 -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-200 rounded-full filter blur-3xl opacity-20 -ml-48 -mb-48"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="slide-up">
              <p className="text-blue-600 font-semibold mb-4 tracking-wide uppercase text-sm animate-fade-in">
                Trusted Financial Growth
              </p>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Our mission is <br />your financial <br />prosperity
              </h1>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                At Morgan Guaranty Trust, we are dedicated to helping you build a secure financial future.
                Our commitment to innovation and integrity ensures your goals are always our top priority.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => navigate('/user/register')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
                >
                  Open Account
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  onClick={() => navigate('/user/login')}
                  variant="outline" 
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
                >
                  Login to Account
                </Button>
              </div>
            </div>
            <div className="relative">
              {/* Image Carousel with Optimized Loading */}
              <div className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] rounded-2xl overflow-hidden shadow-2xl bg-gray-100">
                {heroImages.map((image, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-1000 ${
                      index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <img
                      src={image.url}
                      alt={image.alt}
                      className="w-full h-full object-cover"
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                    />
                  </div>
                ))}
                
                {/* Gradient Overlay for Better Text Visibility on Indicators */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
                
                {/* Carousel Indicators */}
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                  {heroImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`h-1 rounded-full transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'bg-white w-8 shadow-lg' 
                          : 'bg-white/60 w-6 hover:bg-white/90'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
                
                {/* Image Counter Badge */}
                <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
                  {currentImageIndex + 1} / {heroImages.length}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1559659386-5b78a2a4290c"
                alt="Corporate Building"
                className="rounded-2xl shadow-xl w-full"
              />
              <div className="absolute -bottom-8 -right-8 bg-gradient-to-br from-blue-600 to-blue-800 text-white p-6 rounded-xl shadow-xl">
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-2">
                  <div className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">M</span>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <p className="text-blue-600 font-semibold mb-4 tracking-wide uppercase text-sm">About Us</p>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Helping you achieve your financial goals
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                At <strong>Morgan Guaranty Trust</strong>, we are committed to empowering individuals and businesses with secure, innovative, and reliable financial solutions.
                Our mission is to guide you toward long-term financial stability and success through trusted partnerships and expert support.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Professional</h3>
                    <h3 className="font-semibold text-gray-900 mb-2">Financial Services</h3>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Strategic</h3>
                    <h3 className="font-semibold text-gray-900 mb-2">Market Insights</h3>
                  </div>
                </div>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed">
                With years of dedicated service and a strong foundation of trust, we continue to help our clients make confident financial decisions.
                Our focus remains on integrity, innovation, and personalized customer care.
              </p>
              <Link to="/about">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  About Us
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-semibold mb-4 tracking-wide uppercase text-sm">Our Services</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Your trusted financial partner <br />for every stage of life
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="bg-white hover:shadow-xl transition-all duration-300 group border-0 shadow-md">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors duration-300">
                    <Users className="w-8 h-8 text-blue-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <Link to={service.link}>
                    <Button variant="ghost" className="text-blue-600 hover:text-blue-700 p-0">
                      Read more
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <div className="text-6xl font-bold text-gray-100 mt-6">{service.number}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Initiatives Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-blue-600 font-semibold mb-4 tracking-wide uppercase text-sm">Latest Initiatives</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-8">
              Invest in your future <br />with Morgan Guaranty Trust
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInitiatives.map((initiative, index) => (
              <div 
                key={initiative.id} 
                className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-2xl transition-all duration-300"
                style={{
                  contentVisibility: 'auto',
                  containIntrinsicSize: 'auto 300px'
                }}
              >
                <OptimizedImage
                  src={initiative.image}
                  alt={initiative.title}
                  className="w-full h-64"
                  priority={index < 3}
                  blur={true}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-6">
                  <p className="text-blue-400 text-sm font-semibold mb-2">{initiative.subtitle}</p>
                  <h3 className="text-white text-xl font-bold">{initiative.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Support Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full opacity-5 -mr-48 -mt-48"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/351264/pexels-photo-351264.jpeg"
                alt="Banking Building"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -top-8 -left-8 bg-white text-blue-600 p-6 rounded-xl shadow-xl">
                <p className="text-sm font-semibold mb-1">Customer Satisfaction</p>
                <p className="text-4xl font-bold">25+ Years</p>
                <p className="text-sm text-gray-600">of Trusted Banking</p>
              </div>
            </div>
            <div>
              <p className="text-blue-200 font-semibold mb-4 tracking-wide uppercase text-sm">Who We Are</p>
              <h2 className="text-4xl font-bold mb-6">
                Building trust through secure and reliable banking
              </h2>
              <p className="text-blue-100 mb-6 leading-relaxed">
                At <strong>Morgan Guaranty Trust</strong>, we are committed to helping individuals and businesses achieve financial stability and long-term success.
                With decades of proven excellence, our focus remains on transparency, innovation, and customer satisfaction.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-blue-300 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-blue-100">24/7 dedicated customer support — always here when you need us.</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-blue-300 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-blue-100">Trusted financial experts providing personalized solutions for your goals.</span>
                </li>
              </ul>
              <Link to="/contact">
                <Button className="bg-white text-blue-600 hover:bg-blue-50">
                  Learn More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-semibold mb-4 tracking-wide uppercase text-sm">Client Testimonials</p>
            <h2 className="text-4xl font-bold text-gray-900">
              Real experiences from those who trust us
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial) => (
              <Card key={testimonial.id} className="bg-white hover:shadow-xl transition-shadow duration-300 border-0 shadow-md">
                <CardContent className="p-8">
                  <div className="text-blue-600 text-6xl mb-4">"</div>
                  <p className="text-gray-600 mb-6 leading-relaxed">{testimonial.text}</p>
                  <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-semibold mb-4 tracking-wide uppercase text-sm">Our Process</p>
            <h2 className="text-4xl font-bold text-gray-900">
              Invest in your future <br />with confidence
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {processSteps.map((step, index) => (
              <div key={step.id} className="relative">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 hover:shadow-xl transition-shadow duration-300">
                  <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mb-6">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{step.description}</p>
                  <div className="text-6xl font-bold text-blue-200 absolute bottom-8 right-8">{step.number}</div>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-6 transform -translate-y-1/2">
                    <ArrowRight className="w-12 h-12 text-blue-200" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Ticker */}
      <FinancialTicker />
    </div>
  );
};

export default Home;
